/**
 * TagListInput — edit a list of short strings (e.g. qualifications).
 * Enter or the add button appends; each chip has a remove control.
 */

import React, { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { cn } from '../../../utils/cn';

interface TagListInputProps {
	label: string;
	values: string[];
	onChange: (values: string[]) => void;
	placeholder?: string;
	className?: string;
}

export const TagListInput: React.FC<TagListInputProps> = ({ label, values, onChange, placeholder, className }) => {
	const [draft, setDraft] = useState('');

	const add = () => {
		const v = draft.trim();
		if (!v) return;
		if (!values.includes(v)) onChange([...values, v]);
		setDraft('');
	};

	const remove = (idx: number) => onChange(values.filter((_, i) => i !== idx));

	return (
		<div className={className}>
			<label className='mb-1.5 block text-sm font-medium text-gray-700'>{label}</label>
			<div className='flex gap-2'>
				<input
					type='text'
					value={draft}
					onChange={e => setDraft(e.target.value)}
					onKeyDown={e => {
						if (e.key === 'Enter') {
							e.preventDefault();
							add();
						}
					}}
					placeholder={placeholder ?? 'Type and press Enter…'}
					className='h-10 flex-1 rounded-xl border border-gray-300 px-3 text-sm text-gray-900 outline-none focus:border-primary focus:ring-1 focus:ring-primary'
				/>
				<button
					type='button'
					onClick={add}
					className='inline-flex h-10 items-center gap-1 rounded-xl border border-gray-300 px-3 text-sm font-medium text-gray-700 hover:bg-gray-50'
				>
					<Plus className='h-4 w-4' /> Add
				</button>
			</div>
			{values.length > 0 && (
				<div className='mt-2 flex flex-wrap gap-2'>
					{values.map((v, idx) => (
						<span
							key={`${v}-${idx}`}
							className={cn(
								'inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs font-medium text-gray-700'
							)}
						>
							{v}
							<button type='button' onClick={() => remove(idx)} className='text-gray-400 hover:text-gray-600' aria-label={`Remove ${v}`}>
								<X className='h-3 w-3' />
							</button>
						</span>
					))}
				</div>
			)}
		</div>
	);
};
